import React, { FC, useEffect, useMemo } from 'react';

import usePageScroll from '~/src/hooks/usePageScroll';
import usePdfLocalitiesPagination from '~/src/hooks/usePdfLocalitiesPagination';

import StateRegionTable from '~/src/components/CourtForms/StateRegionTable';
import LoadMoreDisplayText from '~/src/components/LoadMoreDisplayText';
import Breadcrumb from '~/src/components/Breadcrumb';

const SCROLL_THRESHOLD_PERCENT = 85;

export const GENERAL_FEDERAL_FORMS_CATEGORY = 'General Federal Forms';

export interface CategoryFolder {
  filterCategory: string;
  locality: string;
}

export const buildCategoryFolders = (
  data: { locality: string; filterCategory: string }[],
): CategoryFolder[] => {
  const folders: CategoryFolder[] = [];
  let hasGeneralForms = false;

  data.forEach((pdfTemplate) => {
    if (pdfTemplate.locality && pdfTemplate.locality !== 'None') {
      folders.push({
        filterCategory: pdfTemplate.filterCategory,
        locality: pdfTemplate.locality,
      });
    } else if (pdfTemplate.locality === 'None') {
      hasGeneralForms = true;
    }
  });

  if (hasGeneralForms) {
    folders.unshift({
      filterCategory: 'FEDERAL',
      locality: GENERAL_FEDERAL_FORMS_CATEGORY,
    });
  }

  return folders;
};

const FEDERAL_FORMS_QUERY = {
  facets: {
    filter_category: 'FEDERAL',
  },
  group_by: 'filter_category,locality',
  query_by: 'locality',
};

interface Props {
  routeUrl?: string;
  showBreadcrumb?: boolean;
  actionType?: 'link' | 'button';
  onCategorySelect?: (categoryLocality: string) => void;
  breadcrumbData?: { label: string; url?: string; onClick?: () => void }[];
}

const FederalFormsWithLocalitiesContainer: FC<Props> = ({
  routeUrl = '/library/court-forms',
  showBreadcrumb = true,
  actionType = 'link',
  onCategorySelect,
  breadcrumbData: externalBreadcrumbData,
}) => {
  const { next, loading, done, data, setSearch } =
    usePdfLocalitiesPagination(FEDERAL_FORMS_QUERY);

  const pageScroll = usePageScroll();

  const loadMore = next;

  useEffect(() => {
    if (
      (pageScroll === 0 || pageScroll >= SCROLL_THRESHOLD_PERCENT) &&
      !loading
    ) {
      loadMore();
    }
  }, [pageScroll, loading, loadMore]);

  const categoryFolders = useMemo(() => buildCategoryFolders(data), [data]);

  const handleSearchInputChange = (searchQuery: string) => {
    setSearch(searchQuery);
  };

  const defaultBreadcrumbData = [
    {
      label: 'Court forms',
      url: routeUrl,
    },
    {
      label: 'Federal forms',
    },
  ];

  const breadcrumbData = externalBreadcrumbData ?? defaultBreadcrumbData;

  return (
    <>
      {showBreadcrumb && <Breadcrumb data={breadcrumbData} />}

      <div className="mb-6">
        <StateRegionTable
          actionType={actionType}
          pdfCourtForms={categoryFolders}
          visibleItems={categoryFolders.length}
          empty={!loading && categoryFolders.length === 0}
          onSearchChange={handleSearchInputChange}
          handleClick={onCategorySelect ?? (() => {})}
          routeUrl={routeUrl}
          columnLabel="Categories"
          searchPlaceholder="Search Categories"
        />
        <LoadMoreDisplayText
          loading={loading}
          done={done}
          onLoadMore={loadMore}
        />
      </div>
    </>
  );
};

export default FederalFormsWithLocalitiesContainer;
